/**
 * Single-property template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';
import BannerHero from './lib/partials/BannerHero';
import PropertyMap from './lib/partials/PropertyMap';
import KeenSlider from 'keen-slider';

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Single-property';
const bannerHeroDom = document.querySelector(`[data-banner-hero]`);
const propertyMapDom = document.querySelector(`[data-property-map]`);

const contactDrawerToggleDoms = document.querySelectorAll('[data-contact-drawer-toggle]');
const contactDrawer = document.querySelector('[data-contact-drawer]');

const galleryModalDom = document.querySelector('[data-gallery-modal]');
const galleryModalCarouselDom = document.querySelector('[data-gallery-modal-carousel]');
const galleryModalToggleDoms = document.querySelectorAll('[data-gallery-modal-toggle]');

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.log(`${NAME} > loaded`);
	initBannerHero();
	initContactDrawerToggle();
	initMap();
	initGalleryModal();
}

/**
 * hideGalleryModal - Hide the gallery modal
 */
const hideGalleryModal = () => {
	Logger.info(`${NAME} > hideGalleryModal`);

	galleryModalDom.classList.remove('single-gallery-modal--active');
	stopBodyScrolling(false);
}

/**
 * initBannerHero - Initialize the banner hero
 */
const initBannerHero = () => {
	Logger.log(`${NAME} > initBannerHero`);

	if (!bannerHeroDom) return;

	const el = new BannerHero(bannerHeroDom);
	el.init();
}

/**
 * initContactDrawerToggle - Initialize the contact drawer toggle
 */
const initContactDrawerToggle = () => {
	Logger.info(`${NAME} > initContactDrawerToggle`);

	if (!contactDrawerToggleDoms || !contactDrawer) return;

	contactDrawerToggleDoms.forEach((contactDrawerToggle) => {
		contactDrawerToggle.addEventListener('click', () => {
			contactDrawer.classList.toggle('single-contact-drawer--active');

			if (contactDrawer.classList.contains('single-contact-drawer--active')) {
				stopBodyScrolling(true);
			} else {
				stopBodyScrolling(false);
			}
		});
	});
}

/**
 * initGalleryModal - Initialize the gallery modal
 */
const initGalleryModal = () => {
	Logger.info(`${NAME} > initGalleryModal`);

	if (!galleryModalDom) return;

	const carousel = new KeenSlider(galleryModalCarouselDom, {
		selector: `[data-gallery-modal-carousel-item]`,
		loop: true,
		slides: {
			perView: () => {
				const totalSlides = galleryModalCarouselDom.querySelectorAll('[data-gallery-modal-carousel-item]').length;
				switch (totalSlides) {
					case 1:
						return 1;
					case 2:
						return 1.25;
					default:
						return 1.25;
				}
			},
			number: () => {
				const totalSlides = galleryModalCarouselDom.querySelectorAll('[data-gallery-modal-carousel-item]').length;
				switch (totalSlides) {
					case 1:
						return 1;
					case 2:
					default:
						return totalSlides;
				}
			},
			origin: 'center',
			spacing: 16,
		},
		breakpoints: {
			'(max-width: 768px)': {
				perView: () => {
					const totalSlides = galleryModalCarouselDom.querySelectorAll('[data-gallery-modal-carousel-item]').length;
					switch (totalSlides) {
						case 1:
							return 1;
						case 2:
							return 1.1;
						default:
							return 1.1;
					}
				},
				number: () => {
					const totalSlides = galleryModalCarouselDom.querySelectorAll('[data-gallery-modal-carousel-item]').length;
					switch (totalSlides) {
						case 1:
							return 1;
						case 2:
						default:
							return totalSlides;
					}
				},
			},
		}
	});

	galleryModalToggleDoms.forEach((galleryModalToggle) => {
		galleryModalToggle.addEventListener('click', () => {

			// Get the index of the clicked item
			const index = galleryModalToggle.dataset.galleryModalToggle;
			const idx = index - 1;

			// Toggle the gallery modal
			showGalleryModal();

			// Set the index of the carousel
			carousel.moveToIdx(idx);
		});
	});

	// If clicked outside of the gallery modal
	galleryModalDom.addEventListener('click', (e) => {
		if (e.target.closest('[data-gallery-modal-carousel-item]') || e.target.closest('[data-gallery-modal-button]')) return;
		hideGalleryModal();
	});

	// If clicked on the previous button
	galleryModalDom.addEventListener('click', (e) => {
		if (e.target.closest('[data-gallery-modal-button]') && e.target.closest('[data-gallery-modal-previous]')) {
			carousel.prev();
		}
	});

	// If clicked on the next button
	galleryModalDom.addEventListener('click', (e) => {
		if (e.target.closest('[data-gallery-modal-button]') && e.target.closest('[data-gallery-modal-next]')) {
			carousel.next();
		}
	});

	// If there is only one slide, hide the previous and next buttons
	// if (galleryModalCarouselDom.querySelectorAll('[data-gallery-modal-carousel-item]').length === 1) {
	// 	galleryModalDom.querySelector('[data-gallery-modal-previous]').style.display = 'none';
	// 	galleryModalDom.querySelector('[data-gallery-modal-next]').style.display = 'none';
	// } else {
	// 	galleryModalDom.querySelector('[data-gallery-modal-previous]').style.display = 'block';
	// 	galleryModalDom.querySelector('[data-gallery-modal-next]').style.display = 'block';
	// }
}

/**
 * initMap - Initialize the PropertyMap partial
 */
const initMap = () => {
	Logger.info(`${NAME} > initMap`);

	if (!propertyMapDom) return;

	const el = new PropertyMap(propertyMapDom);
	el.init();
}

/**
 * showGalleryModal - Show the gallery modal
 */
const showGalleryModal = () => {
	Logger.info(`${NAME} > showGalleryModal`);

	galleryModalDom.classList.add('single-gallery-modal--active');
	stopBodyScrolling(true);
}

/**
 * stopBodyScrolling - Stop the body from scrolling
 */
function stopBodyScrolling(bool) {
	Logger.info(`${NAME} > stopBodyScrolling`);
	if (bool === true) {
		document.body.style.overflow = 'hidden'; document.getElementsByTagName('html')[0].style.overflow = `hidden`;
	} else {
		document.body.style.overflow = 'auto'; document.getElementsByTagName('html')[0].style.overflow = `auto`;
	}
}

// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
